import React from "react";
import { Redirect, Route, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";

// Helper to check if URL is valid for redirect
const isValidRedirectUrl = (url) => {
  if (!url) return false;
  // Don't redirect to login page or empty paths
  const invalidPaths = ['/user-pages/login-1', '/user-pages/login', '/login', '/', ''];
  return !invalidPaths.includes(url);
};

function ProtectedRoute({
  component: Component,
  accesstype = [],
  ...restOfProps
}) {
  const [cookies] = useCookies(["user"]);
  const location = useLocation();
  const isAuthenticated = cookies?.user?.auth === "verified";

  // Save intended URL before redirecting to login (for shared links or direct access).
  //
  // Mirrors the axios interceptor's `saveIntendedUrl` so that front-end role
  // denials get the same loop-protection as backend 401s. Without this, an
  // already-authenticated user landing on /login with a saved deep-link they
  // don't have access to would bounce: login -> forbidden URL -> login -> ...
  const saveIntendedUrl = () => {
    const currentPath = location.pathname + location.search;
    if (!isValidRedirectUrl(currentPath)) {
      return;
    }

    // If this is the URL we just tried to redirect to after login, the user
    // doesn't have access. Mark it as failed (attempts=1) so the next login
    // redirect falls back to the default home and breaks the loop.
    const lastAttemptedRedirect = sessionStorage.getItem('lastAttemptedRedirect');
    if (lastAttemptedRedirect === currentPath) {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
      sessionStorage.setItem('redirectAttempts', '1');
      sessionStorage.removeItem('lastAttemptedRedirect');
      return;
    }

    const existingUrl = sessionStorage.getItem('redirectAfterLogin');
    const redirectAttempts = parseInt(
      sessionStorage.getItem('redirectAttempts') || '0',
      10
    );

    if (existingUrl === currentPath) {
      // Same URL is failing again - hard stop to prevent infinite loops.
      if (redirectAttempts >= 1) {
        sessionStorage.removeItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAttempts');
        sessionStorage.removeItem('lastAttemptedRedirect');
        return;
      }
      sessionStorage.setItem('redirectAttempts', (redirectAttempts + 1).toString());
    } else if (!existingUrl) {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
      sessionStorage.setItem('redirectAttempts', '0');
    }
  };

  return (
    <Route
      {...restOfProps}
      render={(props) => {
        if (isAuthenticated && accesstype?.includes(cookies?.user?.role)) {
          return <Component {...props} />;
        } else {
          // Save intended URL before redirecting to login
          saveIntendedUrl();
          return <Redirect to="/user-pages/login-1" />;
        }
      }}
    />
  );
}

export default ProtectedRoute;
